import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Zap, CreditCard, Crown } from 'lucide-react';
import './PricingPage.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Plan definitions
const PLANS = [
  { id: 'free', name: 'Free', channels: 1, price: 0, features: ['1 YouTube Channel', 'Channel Dashboard', 'Basic Analytics'] },
  { id: 'starter', name: 'Starter', channels: 3, price: 3, features: ['3 YouTube Channels', 'Advanced Analytics', 'Priority Support'] },
  { id: 'creator', name: 'Creator', channels: 5, price: 7, features: ['5 YouTube Channels', 'Team Collaboration', 'API Access'] },
  { id: 'business', name: 'Business', channels: 10, price: 15, features: ['10 YouTube Channels', 'White-label Options', 'Dedicated Support'] },
];

// Tool add-ons
const TOOLS = [
  { id: 'gen_pro', name: 'Gen Pro', price: 15, description: 'AI-powered YouTube playlist creator', icon: '🎬' },
  { id: 'storyteller', name: 'Storyteller', price: 19, description: 'AI video storytelling engine', icon: '📖' },
];

function PricingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [enabledTools, setEnabledTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const stored = localStorage.getItem('vlumina_user');
    if (stored) {
      const userData = JSON.parse(stored);
      setUser(userData);
      setCurrentPlan(userData.plan || 'free');
      // Parse enabled tools if available
      try {
        const tools = JSON.parse(userData.enabled_tools || '[]');
        setEnabledTools(tools);
      } catch {
        setEnabledTools([]);
      }
    }
  }, []);

  const handleUpgradePlan = async (planId) => {
    if (!user) {
      navigate('/signin');
      return;
    }

    if (planId === currentPlan) return;

    // For now, just show a message - in production, this would integrate with Stripe
    setMessage({ 
      type: 'info', 
      text: `To upgrade to ${planId.charAt(0).toUpperCase() + planId.slice(1)}, please contact support@vluminaai.com. Stripe integration coming soon!` 
    });
  };

  const handleToggleTool = async (toolId) => {
    if (!user) {
      navigate('/signin');
      return;
    }

    const isEnabled = enabledTools.includes(toolId);
    
    // For now, just show a message - in production, this would integrate with Stripe
    setMessage({ 
      type: 'info', 
      text: isEnabled 
        ? `To cancel ${toolId}, please contact support@vluminaai.com.`
        : `To add ${toolId}, please contact support@vluminaai.com. Stripe integration coming soon!`
    });
  };

  const calculateTotal = () => {
    const planPrice = PLANS.find(p => p.id === currentPlan)?.price || 0;
    const toolsPrice = enabledTools.reduce((sum, toolId) => {
      const tool = TOOLS.find(t => t.id === toolId);
      return sum + (tool?.price || 0);
    }, 0);
    return planPrice + toolsPrice;
  };

  return (
    <div className="pricing-page">
      <div className="pricing-container">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} /> Back to Dashboard
        </button>

        <h1 className="pricing-title">Billing & Plans</h1>
        <p className="pricing-subtitle">Manage your subscription and add-ons</p>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Current Plan Summary */}
        {user && (
          <div className="current-plan-card">
            <div className="current-plan-header">
              <Crown size={24} className="crown-icon" />
              <div>
                <h3>Current Plan: <span className="plan-name">{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</span></h3>
                <p>Monthly billing • {PLANS.find(p => p.id === currentPlan)?.channels || 1} channel(s)</p>
              </div>
            </div>
            <div className="current-plan-total">
              <span className="total-label">Monthly Total</span>
              <span className="total-amount">${calculateTotal()}/mo</span>
            </div>
          </div>
        )}

        {/* Base Plans */}
        <section className="pricing-section">
          <h2><Zap size={20} /> Base Plans</h2>
          <p className="section-desc">Choose how many channels you need</p>
          
          <div className="plans-grid">
            {PLANS.map((plan) => (
              <div 
                key={plan.id} 
                className={`plan-card ${currentPlan === plan.id ? 'current' : ''}`}
              >
                {currentPlan === plan.id && <div className="current-badge">Current</div>}
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  {plan.price === 0 ? (
                    <span className="price-free">Free</span>
                  ) : (
                    <>
                      <span className="price-amount">${plan.price}</span>
                      <span className="price-period">/mo</span>
                    </>
                  )}
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}><Check size={16} /> {feature}</li>
                  ))}
                </ul>
                <button 
                  className={`btn-plan ${currentPlan === plan.id ? 'current' : ''}`}
                  onClick={() => handleUpgradePlan(plan.id)}
                  disabled={currentPlan === plan.id}
                >
                  {currentPlan === plan.id ? 'Current Plan' : plan.price === 0 ? 'Downgrade' : 'Upgrade'}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Tool Add-ons */}
        <section className="pricing-section">
          <h2><CreditCard size={20} /> Tool Add-ons</h2>
          <p className="section-desc">Supercharge your content creation</p>
          
          <div className="tools-grid">
            {TOOLS.map((tool) => {
              const isEnabled = enabledTools.includes(tool.id);
              return (
                <div key={tool.id} className={`tool-card ${isEnabled ? 'enabled' : ''}`}>
                  <div className="tool-icon">{tool.icon}</div>
                  <div className="tool-info">
                    <h3>{tool.name}</h3>
                    <p>{tool.description}</p>
                    <div className="tool-price">+${tool.price}/mo</div>
                  </div>
                  <button 
                    className={`btn-tool ${isEnabled ? 'enabled' : ''}`}
                    onClick={() => handleToggleTool(tool.id)}
                  >
                    {isEnabled ? 'Active' : 'Add'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Payment Method */}
        <section className="pricing-section">
          <h2><CreditCard size={20} /> Payment Method</h2>
          <div className="payment-placeholder">
            <p>No payment method on file</p>
            <button className="btn-add-payment" onClick={() => setMessage({ type: 'info', text: 'Stripe integration coming soon!' })}>
              Add Payment Method
            </button>
          </div>
        </section>

        {/* Billing History */}
        <section className="pricing-section">
          <h2>Billing History</h2>
          <div className="billing-history">
            <p className="no-history">No billing history yet</p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default PricingPage;
